import { useId, useState } from "react";
import * as api from "../../api/admin";
import { useAdminData, useAdminQuery, useAdminTask } from "./useAdminData";
import {
  ACHIEVEMENT_FIELDS,
  SCHEDULE_FIELDS,
  SHOWCASE_FIELDS,
  TRACK_OPTIONS,
  dateTime,
  downloadFile,
  formPayload,
  fullName,
  initialForm,
  csvCell,
} from "./adminModel";
import {
  ActionMenu,
  AsyncPicker,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EntityEditor,
  FilterSelect,
  Notice,
  Pagination,
  ResourceContent,
  SearchField,
  SectionHeader,
  Toolbar,
} from "./components/AdminUI";
import styles from "./admin.module.css";

const loadShowcase = ({ q, partner, status, track, ...page }) =>
  api.getAdminShowcaseCases({
    ...page,
    query: q,
    partner,
    track,
    is_published: status ? status === "published" : undefined,
  });
const loadAchievements = ({ q, ...page }) =>
  api.getAdminAchievements({ ...page, query: q });
const loadPriorities = ({ q, ...page }) =>
  api.getAdminShowcasePrioritySelections({ ...page, query: q });
const loadSchedule = async ({ q, track, limit, offset }) => {
  const data = await api.getAdminSchedule({ query: q, track_label: track });
  const items = data?.items || [];
  return {
    ...data,
    items: items.slice(offset, offset + limit),
    total: items.length,
  };
};
const timestamp = () => new Date().toISOString().slice(0, 10);
const showcasePayload = (item) =>
  formPayload(SHOWCASE_FIELDS, initialForm(SHOWCASE_FIELDS, item));

export function ShowcaseSection({
  notify,
  navigate,
  initialAction,
  clearAction,
}) {
  const query = useAdminQuery({ partner: "", status: "", track: "" });
  const resource = useAdminData(loadShowcase, query.values);
  const task = useAdminTask();
  const [editor, setEditor] = useState(initialAction === "create" ? {} : null);
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
  };
  const closeEditor = () => {
    setEditor(null);
    clearAction();
  };
  const prepareBulkPublish = () =>
    task.run(
      async () => {
        const items = [];
        let offset = 0;
        while (true) {
          const result = await api.getAdminShowcaseCases({
            is_published: false,
            limit: 100,
            offset,
          });
          if (!result.items?.length) break;
          items.push(...result.items);
          offset += result.items.length;
          if (offset >= result.total) break;
        }
        return items;
      },
      (items) => {
        if (!items.length) {
          notify("Скрытых кейсов нет.");
          return;
        }
        setConfirm({
          title: `Опубликовать кейсы: ${items.length}?`,
          description:
            "Все скрытые кейсы станут доступны в публичной витрине. Текущие фильтры не учитываются.",
          confirmLabel: "Опубликовать все",
          onConfirm: async () => {
            let failed = 0;
            for (let offset = 0; offset < items.length; offset += 10) {
              const results = await Promise.allSettled(
                items.slice(offset, offset + 10).map((item) =>
                  api.updateAdminShowcaseCase(item.case_id, {
                    ...showcasePayload(item),
                    is_published: true,
                  }),
                ),
              );
              failed += results.filter(
                (result) => result.status === "rejected",
              ).length;
            }
            resource.refresh();
            if (failed)
              throw new Error(
                `Опубликовано: ${items.length - failed}. Не удалось опубликовать: ${failed}. Проверьте обязательные поля этих кейсов.`,
              );
            notify(`Опубликовано кейсов: ${items.length}.`);
          },
        });
      },
    );
  const columns = [
    {
      key: "title",
      label: "Кейс",
      render: (item) => (
        <div className={styles.cellStack}>
          <span className={styles.cellMeta}>
            {item.project_code || `№ ${item.case_id}`}
          </span>
          <button
            type="button"
            className={`${styles.cellTitle} ${styles.cellTitleButton}`}
            onClick={() => setEditor(item)}
          >
            {item.title}
          </button>
          <span className={styles.cellDescription}>{item.partner_name}</span>
        </div>
      ),
    },
    {
      key: "track",
      label: "Трек",
      render: (item) => <Badge>{item.track || "Не указан"}</Badge>,
    },
    {
      key: "status",
      label: "Публикация",
      render: (item) => (
        <Badge tone={item.is_published ? "success" : "neutral"}>
          {item.is_published ? "Опубликован" : "Скрыт"}
        </Badge>
      ),
    },
    {
      key: "order",
      label: "Порядок",
      className: styles.hideMedium,
      render: (item) => (
        <span className={styles.cellMono}>{item.sort_order ?? 0}</span>
      ),
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" onClick={() => setEditor(item)}>
            Редактировать
          </Button>
          <ActionMenu
            label={item.title}
            actions={[
              {
                label: item.is_published ? "Скрыть из витрины" : "Опубликовать",
                icon: "globe",
                onClick: () =>
                  setConfirm({
                    title: item.is_published
                      ? "Скрыть кейс?"
                      : "Опубликовать кейс?",
                    description: item.title,
                    confirmLabel: item.is_published ? "Скрыть" : "Опубликовать",
                    onConfirm: async () => {
                      await api.updateAdminShowcaseCase(item.case_id, {
                        ...showcasePayload(item),
                        is_published: !item.is_published,
                      });
                      changed(
                        item.is_published
                          ? "Кейс скрыт из витрины."
                          : "Кейс опубликован.",
                      );
                    },
                  }),
              },
              item.is_published && {
                label: "Открыть в витрине",
                icon: "external",
                onClick: () =>
                  window.open(
                    `/cases?focus=${item.case_id}`,
                    "_blank",
                    "noopener,noreferrer",
                  ),
              },
              {
                label: "Удалить кейс",
                danger: true,
                onClick: () =>
                  setConfirm({
                    title: "Удалить кейс?",
                    description: item.title,
                    confirmLabel: "Удалить кейс",
                    danger: true,
                    children: (
                      <p className={styles.confirmText}>
                        Удаление нельзя отменить. Чтобы временно убрать кейс из
                        витрины, измените его статус на «Скрыт».
                      </p>
                    ),
                    onConfirm: async () => {
                      await api.deleteAdminShowcaseCase(item.case_id);
                      changed("Кейс удалён.");
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Витрина кейсов"
        description="Проектные задачи партнёров: от подготовки до публикации."
      >
        <Button icon="upload" onClick={() => navigate("imports")}>
          Импорт
        </Button>
        <Button variant="primary" icon="plus" onClick={() => setEditor({})}>
          Создать кейс
        </Button>
        <ActionMenu
          label="Витрина"
          actions={[
            {
              label: "Опубликовать все скрытые",
              icon: "globe",
              disabled: task.busy,
              onClick: prepareBulkPublish,
            },
          ]}
        />
      </SectionHeader>
      {task.error && (
        <Notice danger onClose={() => task.setError("")}>
          {task.error}
        </Notice>
      )}
      {task.busy && (
        <p className={styles.hint} role="status">
          Собираем скрытые кейсы…
        </p>
      )}
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={
            query.values.q ||
            query.values.partner ||
            query.values.status ||
            query.values.track
          }
        >
          <SearchField
            placeholder="Найти кейс"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
          <FilterSelect
            label="Статус публикации"
            value={query.values.status}
            onChange={(status) => query.update({ status })}
            options={[
              { value: "", label: "Все статусы" },
              { value: "published", label: "Опубликованные" },
              { value: "hidden", label: "Скрытые" },
            ]}
          />
          <FilterSelect
            label="Трек кейса"
            value={query.values.track}
            onChange={(track) => query.update({ track })}
            options={[{ value: "", label: "Все треки" }, ...TRACK_OPTIONS]}
          />
          <SearchField
            label="Партнёр кейса"
            placeholder="Партнёр"
            value={query.values.partner}
            onChange={(partner) => query.update({ partner })}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Витрина кейсов"
            columns={columns}
            items={resource.items}
            rowKey="case_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {editor && (
        <EntityEditor
          title={editor.case_id ? "Редактирование кейса" : "Новый кейс"}
          description={
            editor.case_id
              ? editor.project_code || `Кейс № ${editor.case_id}`
              : "Заполните основное. Остальные детали можно добавить позже."
          }
          item={editor}
          fields={SHOWCASE_FIELDS}
          onClose={closeEditor}
          onSave={(payload) =>
            editor.case_id
              ? api.updateAdminShowcaseCase(editor.case_id, payload)
              : api.createAdminShowcaseCase(payload)
          }
          onSaved={() => changed("Кейс сохранён.")}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

export function ScheduleSection({
  notify,
  navigate,
  initialAction,
  clearAction,
}) {
  const query = useAdminQuery({ track: "" });
  const resource = useAdminData(loadSchedule, query.values);
  const task = useAdminTask();
  const [editor, setEditor] = useState(initialAction === "create" ? {} : null);
  const [recording, setRecording] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
  };
  const closeEditor = () => {
    setEditor(null);
    clearAction();
  };
  const columns = [
    {
      key: "title",
      label: "Событие",
      render: (item) => (
        <div className={styles.cellStack}>
          <button
            type="button"
            className={`${styles.cellTitle} ${styles.cellTitleButton}`}
            onClick={() => setEditor(item)}
          >
            {item.title}
          </button>
          <span className={styles.cellMeta}>
            {item.presenter_name || "Спикер не указан"}
            {item.location ? ` · ${item.location}` : ""}
          </span>
          {item.is_control_event && (
            <Badge tone="primary">Контрольное мероприятие</Badge>
          )}
        </div>
      ),
    },
    {
      key: "date",
      label: "Дата и время",
      render: (item) => (
        <div className={styles.cellStack}>
          <span>{item.date_label || "Дата не указана"}</span>
          <span className={styles.cellMeta}>
            {item.time_label || "Время уточняется"}
          </span>
        </div>
      ),
    },
    {
      key: "track",
      label: "Трек",
      render: (item) => <Badge>{item.track_label}</Badge>,
    },
    {
      key: "recording",
      label: "Запись",
      className: styles.hideMedium,
      render: (item) => (
        <Badge tone={item.recording_url ? "success" : "neutral"}>
          {item.recording_url ? "Добавлена" : "Нет записи"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" onClick={() => setEditor(item)}>
            Редактировать
          </Button>
          <ActionMenu
            label={item.title}
            actions={[
              {
                label: item.recording_url
                  ? "Изменить запись"
                  : "Добавить запись",
                icon: "edit",
                onClick: () => setRecording(item),
              },
              {
                label: "Экспорт цифрового следа",
                icon: "download",
                disabled: task.busy,
                onClick: () =>
                  task.run(
                    () => api.exportAdminScheduleEntry(item.entry_id),
                    (blob) => {
                      downloadFile(
                        blob,
                        `digital-trace-${item.entry_id}-${timestamp()}.xlsx`,
                      );
                      notify("Цифровой след выгружен.");
                    },
                  ),
              },
              {
                label: "Удалить событие",
                danger: true,
                onClick: () =>
                  setConfirm({
                    title: "Удалить событие?",
                    description: item.title,
                    confirmLabel: "Удалить событие",
                    danger: true,
                    onConfirm: async () => {
                      await api.deleteAdminScheduleEntry(item.entry_id);
                      changed("Событие удалено.");
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Расписание"
        description="События интенсива, встречи, записи и задания для цифрового следа."
      >
        <Button icon="upload" onClick={() => navigate("imports")}>
          Импорт
        </Button>
        <Button variant="primary" icon="plus" onClick={() => setEditor({})}>
          Создать событие
        </Button>
      </SectionHeader>
      {task.error && (
        <Notice danger onClose={() => task.setError("")}>
          {task.error}
        </Notice>
      )}
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q || query.values.track}
        >
          <SearchField
            placeholder="Тема, спикер или место"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
          <FilterSelect
            label="Трек события"
            value={query.values.track}
            onChange={(track) => query.update({ track })}
            options={[
              { value: "", label: "Все треки" },
              ...Array.from(
                new Set([
                  ...(resource.data?.tracks || []),
                  ...(query.values.track ? [query.values.track] : []),
                ]),
              ).map((track) => ({ value: track, label: track })),
            ]}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Расписание интенсива"
            columns={columns}
            items={resource.items}
            rowKey="entry_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {editor && (
        <EntityEditor
          title={editor.entry_id ? "Редактирование события" : "Новое событие"}
          description="Время и место, ссылки на встречу и материалы — в одной карточке."
          item={editor}
          fields={SCHEDULE_FIELDS.map((field) =>
            field.name === "track_label"
              ? { ...field, suggestions: resource.data?.tracks || [] }
              : field,
          )}
          onClose={closeEditor}
          onSave={(payload) =>
            editor.entry_id
              ? api.updateAdminScheduleEntry(editor.entry_id, payload)
              : api.createAdminScheduleEntry(payload)
          }
          onSaved={() => changed("Событие сохранено.")}
        />
      )}
      {recording && (
        <EntityEditor
          title="Запись события"
          description={recording.title}
          item={recording}
          fields={SCHEDULE_FIELDS.filter((field) =>
            ["recording_url", "video_url", "video_preview_url"].includes(
              field.name,
            ),
          )}
          onClose={() => setRecording(null)}
          onSave={(payload) =>
            api.updateAdminScheduleEntry(recording.entry_id, payload)
          }
          onSaved={() => changed("Запись события сохранена.")}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

function AchievementAssignment({ achievement, revoke, onSaved, onClose }) {
  const [person, setPerson] = useState(null);
  const task = useAdminTask();
  const id = useId();
  return (
    <Dialog
      compact
      title={revoke ? "Отозвать достижение" : "Выдать достижение"}
      description={achievement.title}
      busy={task.busy}
      onClose={onClose}
      footer={
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button onClick={onClose} disabled={task.busy}>
              Отмена
            </Button>
            <Button
              form={id}
              type="submit"
              variant={revoke ? "danger" : "primary"}
              disabled={!person || task.busy}
            >
              {task.busy ? "Выполняем…" : revoke ? "Отозвать" : "Выдать"}
            </Button>
          </div>
        </>
      }
    >
      <form
        id={id}
        onSubmit={(event) => {
          event.preventDefault();
          if (!person) return;
          task.run(
            () =>
              (revoke
                ? api.revokeAdminAchievementFromUser
                : api.assignAdminAchievementToUser)(
                achievement.achievement_id,
                person.user_id,
              ),
            () => {
              onSaved(revoke ? "Достижение отозвано." : "Достижение выдано.");
              onClose();
            },
          );
        }}
      >
        <AsyncPicker
          label="Пользователь"
          loader={api.getAdminUsers}
          value={person}
          onChange={setPerson}
          getLabel={fullName}
          getMeta={(item) => `@${item.login} · ${item.email}`}
          idKey="user_id"
          required
          disabled={task.busy}
        />
      </form>
    </Dialog>
  );
}
export function AchievementsSection({ notify, initialAction, clearAction }) {
  const query = useAdminQuery();
  const resource = useAdminData(loadAchievements, query.values);
  const [editor, setEditor] = useState(initialAction === "create" ? {} : null);
  const [assignment, setAssignment] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
  };
  const columns = [
    {
      key: "title",
      label: "Достижение",
      render: (item) => (
        <div className={styles.cellStack}>
          <button
            type="button"
            className={`${styles.cellTitle} ${styles.cellTitleButton}`}
            onClick={() => setEditor(item)}
          >
            {item.title}
          </button>
          <span className={styles.cellDescription}>
            {item.description || "Без описания"}
          </span>
        </div>
      ),
    },
    {
      key: "date",
      label: "Создано",
      render: (item) => (
        <span className={styles.cellMeta}>{dateTime(item.created_at)}</span>
      ),
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button
            onClick={() => setAssignment({ achievement: item, revoke: false })}
          >
            Выдать
          </Button>
          <ActionMenu
            label={item.title}
            actions={[
              {
                label: "Редактировать",
                icon: "edit",
                onClick: () => setEditor(item),
              },
              {
                label: "Отозвать у пользователя",
                icon: "users",
                onClick: () =>
                  setAssignment({ achievement: item, revoke: true }),
              },
              {
                label: "Удалить достижение",
                danger: true,
                onClick: () =>
                  setConfirm({
                    title: "Удалить достижение?",
                    description: item.title,
                    confirmLabel: "Удалить достижение",
                    danger: true,
                    onConfirm: async () => {
                      await api.deleteAdminAchievement(item.achievement_id);
                      changed("Достижение удалено.");
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Достижения"
        description="Награды за участие и вклад в проекты. Создавайте и выдавайте участникам."
      >
        <Button variant="primary" icon="plus" onClick={() => setEditor({})}>
          Создать достижение
        </Button>
      </SectionHeader>
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q}
        >
          <SearchField
            placeholder="Название достижения"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Достижения"
            columns={columns}
            items={resource.items}
            rowKey="achievement_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {editor && (
        <EntityEditor
          title={
            editor.achievement_id
              ? "Редактирование достижения"
              : "Новое достижение"
          }
          fields={ACHIEVEMENT_FIELDS}
          item={editor}
          onClose={() => {
            setEditor(null);
            clearAction();
          }}
          onSave={(payload) =>
            editor.achievement_id
              ? api.updateAdminAchievement(editor.achievement_id, payload)
              : api.createAdminAchievement(payload)
          }
          onSaved={() => changed("Достижение сохранено.")}
        />
      )}
      {assignment && (
        <AchievementAssignment
          {...assignment}
          onClose={() => setAssignment(null)}
          onSaved={notify}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

export function PrioritiesSection({ notify }) {
  const query = useAdminQuery();
  const resource = useAdminData(loadPriorities, query.values);
  const task = useAdminTask();
  const exportPriorities = () =>
    task.run(
      async () => {
        const allItems = [];
        let offset = 0;
        while (true) {
          const response = await api.getAdminShowcasePrioritySelections({
            query: query.values.q,
            limit: 500,
            offset,
          });
          if (!response.items?.length) break;
          allItems.push(...response.items);
          offset += response.items.length;
          if (offset >= response.total) break;
        }
        const rows = [
          [
            "ФИО",
            "Почта",
            "Логин",
            "Выбрано кейсов",
            "Высокий",
            "Средний",
            "Низкий",
            "Обновлено",
          ],
          ...allItems.map((item) => [
            fullName(item),
            item.email,
            item.login,
            item.selected_count,
            item.p1_case_title,
            item.p2_case_title,
            item.p3_case_title,
            dateTime(item.updated_at),
          ]),
        ];
        downloadFile(
          new Blob(
            [
              `\uFEFF${rows.map((row) => row.map(csvCell).join(";")).join("\r\n")}`,
            ],
            { type: "text/csv;charset=utf-8;" },
          ),
          `prioritety-${timestamp()}.csv`,
        );
        return allItems.length;
      },
      (count) => notify(`Выгружены приоритеты участников: ${count}.`),
    );
  const columns = [
    {
      key: "name",
      label: "Участник",
      render: (item) => (
        <div className={styles.cellStack}>
          <strong className={styles.cellTitle}>{fullName(item)}</strong>
          <span className={styles.cellMeta}>{item.email}</span>
          <span className={styles.cellMeta}>@{item.login}</span>
        </div>
      ),
    },
    {
      key: "p1",
      label: "Высокий приоритет",
      render: (item) => (
        <span className={styles.cellDescription}>
          {item.p1_case_title || "Не выбран"}
        </span>
      ),
    },
    {
      key: "p2",
      label: "Средний приоритет",
      render: (item) => (
        <span className={styles.cellDescription}>
          {item.p2_case_title || "Не выбран"}
        </span>
      ),
    },
    {
      key: "p3",
      label: "Низкий приоритет",
      render: (item) => (
        <span className={styles.cellDescription}>
          {item.p3_case_title || "Не выбран"}
        </span>
      ),
    },
    {
      key: "count",
      label: "Выбрано",
      render: (item) => (
        <div className={styles.cellStack}>
          <Badge tone="primary">{item.selected_count ?? 0}</Badge>
          <span className={styles.cellMeta}>{dateTime(item.updated_at)}</span>
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Приоритеты участников"
        description="Какие проекты выбирают участники и насколько им интересен каждый кейс."
      >
        <Button icon="download" disabled={task.busy} onClick={exportPriorities}>
          {task.busy ? "Готовим выгрузку…" : "Экспорт CSV"}
        </Button>
      </SectionHeader>
      {task.error && (
        <Notice danger onClose={() => task.setError("")}>
          {task.error}
        </Notice>
      )}
      <div className={styles.helpBanner}>
        Экспорт включает все записи по текущему поиску, а не только открытую
        страницу.
      </div>
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q}
        >
          <SearchField
            placeholder="Имя, почта или название кейса"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Приоритеты участников"
            columns={columns}
            items={resource.items}
            rowKey="user_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
    </>
  );
}
